import { GAME_CONFIG } from "../config/gameConfig.js";
import { getRoomAt, getRooms } from "../systems/rooms.js";
import { getLightsDebug } from "../systems/lighting.js";
import { isDebugModeVisible, setDebugModeVisible } from "../systems/debugState.js";
import { getPositionAnchorDebugCell } from "../systems/level.js";
import {
    setPerfEnabled,
    perfFrameBegin,
    perfFrameEnd,
    getPerfSnapshot,
    togglePerfFlag,
    dumpPerfToConsole,
} from "../systems/perf.js";

/**
 * Debug overlay — world/tile coords + room id + frame profiler.
 * Toggle with backtick (`).
 *
 * Profiler keys (while HUD visible):
 *   1  — toggle lighting render (A/B)
 *   p  — dump snapshot to console
 */

let hudRoot = null;
let label = null;
let perfLabel = null;
let lightGfx = [];
let roomGfx = [];
let enabled = true;
let lastSnap = null;

const COLLISION_BOUND_COLORS = {
    player: () => rgb(80, 255, 120),
    guard_robot: () => rgb(255, 70, 70),
};

function clearRoomGfx() {
    for (const g of roomGfx) {
        if (g.exists?.()) destroy(g);
    }
    roomGfx = [];
}

function clearLightGfx() {
    for (const g of lightGfx) {
        if (g.exists?.()) destroy(g);
    }
    lightGfx = [];
}

function drawLightGidLabels() {
    clearLightGfx();
    if (!enabled) return;

    const tw = GAME_CONFIG.level.tileWidth;
    const th = GAME_CONFIG.level.tileHeight;

    for (const light of getLightsDebug()) {
        if (light.gid == null) continue;
        const tag = add([
            text(`L${light.gid}`, { font: "ShareTechMono", size: 10 }),
            pos(light.pos.x - tw * 0.25, light.pos.y - th * 0.6),
            color(255, 220, 80),
            opacity(0.95),
            z(8802),
            "debug_light_gid",
        ]);
        lightGfx.push(tag);
    }
}

function getCollisionBoundColor(obj) {
    if (obj.is?.("player")) return COLLISION_BOUND_COLORS.player();
    return COLLISION_BOUND_COLORS.guard_robot();
}

function shouldDrawCollisionBounds(obj) {
    if (!obj?.exists?.()) return false;
    if (!obj.has?.("area")) return false;
    if (obj.fixed) return false;
    return obj.is?.("player") || obj.is?.("guard_robot");
}

function drawCollisionBounds() {
    if (!enabled) return;
    if (GAME_CONFIG.debug?.collisionBounds === false) return;

    const candidates = [
        ...get("player"),
        ...get("guard_robot"),
    ];

    for (const obj of candidates) {
        if (!shouldDrawCollisionBounds(obj)) continue;

        const bbox = obj.worldBbox?.();
        if (!bbox || bbox.width <= 0 || bbox.height <= 0) continue;

        const boundColor = getCollisionBoundColor(obj);
        drawRect({
            pos: bbox.pos,
            width: bbox.width,
            height: bbox.height,
            fill: false,
            color: boundColor,
            outline: {
                width: 1,
                color: boundColor,
                opacity: 0.95,
            },
        });
    }
}

function drawRoomOutlines() {
    clearRoomGfx();
    if (!enabled) return;
    if (GAME_CONFIG.debug?.roomOutlines !== true) return;

    const tw = GAME_CONFIG.level.tileWidth;
    const th = GAME_CONFIG.level.tileHeight;

    for (const room of getRooms()) {
        const r = room.tileRect;
        const box = add([
            rect(r.w * tw, r.h * th),
            pos(r.x * tw, r.y * th),
            anchor("topleft"),
            outline(1, rgb(0, 255, 200)),
            color(0, 255, 200),
            opacity(0.08),
            z(8800),
            "debug_room_outline",
        ]);
        const tag = add([
            text(`R${room.id}`, { font: "ShareTechMono", size: 10 }),
            pos(r.x * tw + 4, r.y * th + 4),
            color(0, 255, 200),
            opacity(0.85),
            z(8801),
            "debug_room_outline",
        ]);
        roomGfx.push(box, tag);
    }
}

function fmtMs(ms) {
    if (ms == null || Number.isNaN(ms)) return "—";
    return ms < 10 ? ms.toFixed(2) : ms.toFixed(1);
}

function formatPerfLines(snap) {
    const light = snap.buckets.lighting;
    const fog = snap.buckets.roomFog;
    const vision = snap.buckets.vision;
    const lightOn = snap.flags.lighting !== false;
    const tiles = snap.stats.mapTiles ?? "—";
    const mapSize = snap.stats.mapSize ?? "—";

    return [
        `fps:${snap.fps.toFixed(0)}  wall:${fmtMs(snap.frameMs)}ms  engDt:${fmtMs(snap.engineDtMs)}ms  ents:${snap.entityCount}  tiles:${tiles} (${mapSize})`,
        `baked:${snap.stats.bakedTiles ?? "—"}/${snap.stats.bakedLayers ?? "—"}L  pathGrid:${snap.stats.pathCells ?? "—"}  light:${fmtMs(light?.avgMs)}ms${lightOn ? "" : " OFF"}  fog:${fmtMs(fog?.avgMs)}ms  dpr:${snap.dpr.toFixed(2)}`,
        `1:toggle light  p:console dump  |  engDt caps at 100ms when lagged`,
    ].join("\n");
}

export function isDebugHUDVisible() {
    return isDebugModeVisible();
}

export function initDebugHUD() {
    destroyAll("debug_hud");
    destroyAll("debug_room_outline");
    destroyAll("debug_light_gid");
    roomGfx = [];
    lightGfx = [];
    lastSnap = null;

    enabled = GAME_CONFIG.debug?.hud !== false;
    setDebugModeVisible(enabled);
    setPerfEnabled(enabled);

    hudRoot = add([
        fixed(),
        z(9900),
        "debug_hud",
    ]);

    label = hudRoot.add([
        text("", { font: "ShareTechMono", size: 12 }),
        pos(12, height() - 108),
        color(180, 255, 160),
        opacity(0.95),
    ]);

    perfLabel = hudRoot.add([
        text("", { font: "ShareTechMono", size: 11 }),
        pos(12, height() - 88),
        color(160, 220, 255),
        opacity(0.95),
    ]);

    const hint = hudRoot.add([
        text("` debug  |  yellow L# = Light GID  |  profiler on", {
            font: "ShareTechMono",
            size: 10,
        }),
        pos(12, height() - 20),
        color(120, 160, 140),
        opacity(0.8),
    ]);

    onKeyPress("`", () => {
        enabled = !enabled;
        setDebugModeVisible(enabled);
        setPerfEnabled(enabled);
        if (hudRoot) hudRoot.hidden = !enabled;
        if (enabled) {
            drawRoomOutlines();
            drawLightGidLabels();
        } else {
            clearRoomGfx();
            clearLightGfx();
        }
    });

    onKeyPress("1", () => {
        if (!enabled) return;
        const on = togglePerfFlag("lighting");
        console.log(`[perf] lighting ${on ? "ON" : "OFF"}`);
    });

    onKeyPress("p", () => {
        if (!enabled) return;
        dumpPerfToConsole();
    });

    onUpdate(() => {
        if (!enabled || !label) return;

        perfFrameBegin();

        label.pos = vec2(12, height() - 108);
        if (perfLabel) perfLabel.pos = vec2(12, height() - 88);
        hint.pos = vec2(12, height() - 20);

        const player = get("player")[0];
        if (!player) {
            label.text = "pos: —";
            if (perfLabel) perfLabel.text = "";
            perfFrameEnd();
            return;
        }

        const tw = GAME_CONFIG.level.tileWidth;
        const th = GAME_CONFIG.level.tileHeight;
        const tx = Math.floor(player.pos.x / tw);
        const ty = Math.floor(player.pos.y / th);
        const anchorCell = getPositionAnchorDebugCell();
        const rtx = tx - anchorCell.x;
        const rty = ty - anchorCell.y;
        const room = getRoomAt(player.pos);
        const roomStr = room ? `R${room.id}` : "gap";

        const roomLights = getLightsDebug().filter(
            (l) => l.gid != null && l.roomId === room?.id,
        );
        const lightStr = roomLights.length
            ? roomLights.map((l) => `L${l.gid}`).join(",")
            : "none";

        label.text =
            `x:${player.pos.x.toFixed(0)} y:${player.pos.y.toFixed(0)}` +
            `  tile:${rtx},${rty}` +
            `  abs:${tx},${ty}` +
            `  room:${roomStr}` +
            `  lights:${lightStr}`;

        lastSnap = getPerfSnapshot();
        if (perfLabel) {
            perfLabel.text = formatPerfLines(lastSnap);
        }

        perfFrameEnd();
    });

    onDraw(drawCollisionBounds);

    hudRoot.hidden = !enabled;
    if (enabled) {
        drawRoomOutlines();
        drawLightGidLabels();
    }
}
